import React, {FC, ReactNode, useMemo, useState} from 'react';

import {buildInteractionIdProps} from '../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {
  InteractionParent,
  InteractionParentProps,
} from '../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {scrollbar} from '../../../libs/shared/ui/src/styles/mixins';
import {styled, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {NewPanelId} from '../../store/panels/panelStateBase';
import {Grid, GridItem} from '../common/grid';
import {DynamicScrollBoundaries} from '../common/scroll/dynamicScrollBoundaries';
import {LeftPanelContext} from './leftPanelContext';

/*
 * Props.
 */

interface LeftPanelProps extends InteractionParentProps {
  panelId: NewPanelId | undefined;
  /**
   * Consider wrapping topBar node(s) in a LeftPanelTopbar.
   * @see LeftPanelTopbar
   */
  topBar?: ReactNode;
  bottomBar?: ReactNode;
}

/*
 * Style.
 */

const StyledLayoutGrid = styled(Grid)`
  grid-template-rows: auto 1fr auto;
  background-color: ${(p) => p.theme.background};
`;

const StyledMiddleScrollBoundaries = styled(DynamicScrollBoundaries)`
  grid-row: 2;
  grid-column: 1;
`;

const StyledMiddleScrollerDiv = styled.div`
  position: relative;
  overflow: auto;
  ${(p) => scrollbar(p.theme.scrollbarTrack, p.theme.scrollbarThumb, p.theme.scrollbarThumbHover)}

  padding-bottom: 53px;
`;

const StyledEmptyTopBarPadding = styled.div`
  padding: 10px 0px;
`;

/*
 * Component.
 */

export const LeftPanel: FC<LeftPanelProps> = (props) => {
  const {interactionId, children, topBar, bottomBar} = props;
  const {itemSeparator} = useUserTheme();
  const [scrollerElement, setScrollerElement] = useState<HTMLElement | null>(null);
  const context = useMemo(() => ({scrollerElement}), [scrollerElement]);

  const interactionIdProps = buildInteractionIdProps('workspaces-with-p1-declutter');

  return (
    <InteractionParent interactionId={interactionId}>
      <StyledLayoutGrid {...interactionIdProps}>
        <GridItem row={1}>{topBar || <StyledEmptyTopBarPadding />}</GridItem>
        <StyledMiddleScrollBoundaries color={itemSeparator}>
          <StyledMiddleScrollerDiv ref={setScrollerElement}>
            <LeftPanelContext.Provider value={context}>{children}</LeftPanelContext.Provider>
          </StyledMiddleScrollerDiv>
        </StyledMiddleScrollBoundaries>
        {Boolean(bottomBar) && <GridItem row={3}>{bottomBar}</GridItem>}
      </StyledLayoutGrid>
    </InteractionParent>
  );
};
